import React from 'react';
import styled, { css } from 'styled-components';

type ButtonVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark';
type ButtonSize = 'small' | 'medium' | 'large';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

const StyledButton = styled.button<ButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  border: none;
  border-radius: var(--radius-md);
  transition: all var(--transition-fast);
  cursor: pointer;
  gap: 8px;
  
  /* Handle disabled state */
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  /* Size variations */
  ${({ size }) => {
    switch (size) {
      case 'small':
        return css`
          padding: 6px 12px;
          font-size: var(--font-sm);
        `;
      case 'large':
        return css`
          padding: 12px 24px;
          font-size: var(--font-lg);
        `;
      case 'medium':
      default:
        return css`
          padding: 8px 16px;
          font-size: var(--font-md);
        `;
    }
  }}
  
  /* Variant styles */
  ${({ variant }) => {
    switch (variant) {
      case 'secondary':
        return css`
          background-color: var(--secondary-color);
          color: white;
          &:hover:not(:disabled) {
            background-color: #e08630;
            box-shadow: var(--shadow-sm);
          }
          &:active:not(:disabled) {
            background-color: #cc7920;
          }
        `;
      case 'success':
        return css`
          background-color: var(--success-color);
          color: white;
          &:hover:not(:disabled) {
            background-color: #2a9265;
            box-shadow: var(--shadow-sm);
          }
          &:active:not(:disabled) {
            background-color: #1e7a51;
          }
        `;
      case 'danger':
        return css`
          background-color: var(--danger-color);
          color: white;
          &:hover:not(:disabled) {
            background-color: #e64545;
            box-shadow: var(--shadow-sm);
          }
          &:active:not(:disabled) {
            background-color: #cc3333;
          }
        `;
      case 'warning':
        return css`
          background-color: var(--warning-color);
          color: var(--text-primary);
          &:hover:not(:disabled) {
            background-color: #e6b800;
            box-shadow: var(--shadow-sm);
          }
          &:active:not(:disabled) {
            background-color: #cca300;
          }
        `;
      case 'info':
        return css`
          background-color: var(--info-color);
          color: white;
          &:hover:not(:disabled) {
            background-color: #3385e6;
            box-shadow: var(--shadow-sm);
          }
          &:active:not(:disabled) {
            background-color: #1a70cc;
          }
        `;
      case 'light':
        return css`
          background-color: var(--bg-light);
          color: var(--text-primary);
          border: 1px solid var(--border-light);
          &:hover:not(:disabled) {
            background-color: var(--bg-subtle);
            box-shadow: var(--shadow-sm);
          }
          &:active:not(:disabled) {
            background-color: #e6e8ed;
          }
        `;
      case 'dark':
        return css`
          background-color: var(--text-primary);
          color: white;
          &:hover:not(:disabled) {
            background-color: #0f1d33;
            box-shadow: var(--shadow-sm);
          }
          &:active:not(:disabled) {
            background-color: #070e19;
          }
        `;
      case 'primary':
      default:
        return css`
          background-color: var(--primary-color);
          color: white;
          &:hover:not(:disabled) {
            background-color: #3385e6;
            box-shadow: var(--shadow-sm);
          }
          &:active:not(:disabled) {
            background-color: #1a70cc;
          }
        `;
    }
  }}
  
  /* Full width */
  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `}
    
  /* Loading state */
  ${({ loading }) =>
    loading &&
    css`
      position: relative;
      color: transparent !important;
      pointer-events: none;
      
      &::after {
        content: '';
        position: absolute;
        width: 16px;
        height: 16px;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        margin: auto;
        border: 2px solid transparent;
        border-top-color: currentColor;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
      }
      
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `}
    
  /* Icon position */
  ${({ iconPosition }) => {
    if (iconPosition === 'right') {
      return css`
        flex-direction: row-reverse;
      `;
    }
    return css`
      flex-direction: row;
    `;
  }}
`;

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  loading = false,
  icon,
  iconPosition = 'left',
  ...props
}) => {
  return (
    <StyledButton
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      loading={loading}
      iconPosition={iconPosition}
      disabled={props.disabled || loading}
      {...props}
    >
      {icon && icon}
      {children}
    </StyledButton>
  );
};

export default Button; 